import React, { useEffect, useRef, useState } from 'react'
import Select from 'components/select'
import Field from 'components/field'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { getProjectTables } from 'helpers/utils'
import api from 'helpers/api'
import { ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS, BLOCK_SELECT_OPTION_TYPES } from 'components/spreadsheet/constants/const'
import { RESTRICTED_SOURCE_PLACEHOLDER } from 'app-constants'
import { ITable, ITableViewWithColumns } from 'types'
import { Spreadsheet } from 'components/icons'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'

interface SourceOption {
  label: string
  value: string
  icon?: React.ReactNode
}

// How a picker labels its selected id. Normally that's the matching option, but a source configured by
// another admin can be missing from the options: the table and view lists only hold what the user can open
// at the table level, while the source view itself may still be readable through a view-level role (the
// same rule the backend applies to lookup sources). Then the name the view fetch returned is shown instead.
// Only when neither is available is the source restricted, and it shows the redacted placeholder (the same
// one lookups use) rather than a blank select. Neither label is offered as a pickable option.
const getSelection = (
  selectedId: string,
  options: SourceOption[] | undefined,
  placeholder: string,
  sourceLabel?: string
): { optionsSelected: string[]; placeholder: string } => {
  if (!selectedId || !options || options.some((option) => option.value === selectedId)) {
    return { optionsSelected: selectedId ? [selectedId] : [], placeholder }
  }
  if (sourceLabel) return { optionsSelected: [], placeholder: sourceLabel }
  return { optionsSelected: [], placeholder: RESTRICTED_SOURCE_PLACEHOLDER }
}

interface SelectSourceFieldsProps {
  // Current selection (controlled by the consumer).
  tableId: string
  viewId: string
  columnId: string
  onTableChange: (tableId: string) => void
  onViewChange: (viewId: string) => void
  onColumnChange: (columnId: string) => void
  disabled?: boolean
  // The selected source view as the user can read it (with its columns and table), once it has loaded.
  onViewLoaded?: (view: ITableViewWithColumns) => void
}

// The shared "source table → view → column" dependent dropdowns for a column that sources its options
// from another table. Owns the tables/views/columns fetches; the selection itself is controlled by the
// consumer. Views are limited to Spreadsheet views and column options exclude kinds that can't be a
// select source (BLOCK_SELECT_OPTION_TYPES).
const SelectSourceFields: React.FC<SelectSourceFieldsProps> = ({
  tableId,
  viewId,
  columnId,
  onTableChange,
  onViewChange,
  onColumnChange,
  disabled = false,
  onViewLoaded
}) => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()

  const [tables, setTables] = useState<ITable[] | undefined>(undefined)
  // The tables as last fetched, for the views fetch to find a table's default view without depending on them.
  const tablesRef = useRef<ITable[] | undefined>(undefined)
  useEffect(() => {
    tablesRef.current = tables
  }, [tables])
  const [views, setViews] = useState<ITableViewWithColumns[] | undefined>(undefined)
  // The last view fetched (null when the user can't read it), keyed by its id so a result is never read
  // against a different selection while the next fetch is in flight.
  const [fetchedView, setFetchedView] = useState<{ viewId: string; view: ITableViewWithColumns | null }>()

  // The store's error reporter and the consumer's callbacks kept in a ref (refreshed in an effect, never
  // written during render — a React Compiler rule) so the id-keyed fetch effects don't list them as deps
  // and re-run whenever an inline callback's identity changes.
  const handlersRef = useRef({ displayErrorMessage, onViewLoaded, onViewChange, viewId })
  useEffect(() => {
    handlersRef.current = { displayErrorMessage, onViewLoaded, onViewChange, viewId }
  })

  // Until the source view has loaded it isn't known whether a table/view missing from its list is readable
  // through that view, so neither is judged restricted yet.
  const sourceView = viewId && fetchedView?.viewId === viewId ? fetchedView.view : undefined
  const viewPending = Boolean(viewId) && sourceView === undefined
  const columns = sourceView === null ? [] : sourceView?.columns
  // Each table with the table icon the sidebar gives it, and each column with its type's icon, as its header
  // and Edit column show them.
  const tableOptions = tables?.map((table) => ({ label: table.name, value: table.publicId, icon: <Spreadsheet /> }))
  const viewOptions = views?.map((view) => ({ label: view.name, value: view.publicId }))
  const columnOptions = columns
    ?.filter((column) => !BLOCK_SELECT_OPTION_TYPES.includes(column.kind))
    .map((column) => ({ label: column.name, value: column.publicId, icon: <IconSelector kind={column.kind} /> }))

  const tableSelection = getSelection(
    tableId,
    viewPending ? undefined : tableOptions,
    'Select a table',
    sourceView?.documentTable?.publicId === tableId ? sourceView.documentTable.name : undefined
  )
  const viewSelection = getSelection(viewId, viewPending ? undefined : viewOptions, 'Select a view', sourceView?.name)
  const columnSelection = getSelection(columnId, columnOptions, 'Select a column')

  // Tables for the project.
  useEffect(() => {
    getProjectTables(project.publicId, false)
      .then(setTables)
      .catch((error) => handlersRef.current.displayErrorMessage(error))
  }, [project.publicId])

  // Views for the selected table (Spreadsheet views only), refetched whenever the table changes.
  useEffect(() => {
    setViews(undefined)
    if (!tableId) return
    // A table picked after this one makes its views stale: they're ignored when they arrive.
    let stale = false
    api
      .getTableViews(tableId, { ignoreColumns: true })
      .then((response) => {
        if (stale) return
        const allowed = response.data
          .filter((view) => ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS.includes(view.type))
          .sort((viewA, viewB) => viewA.name.localeCompare(viewB.name))
        setViews(allowed)
        // A table just picked, with no view yet: its default view, which is the one wanted nearly always, or
        // its first that can be a source when the default can't (a form, say).
        if (!handlersRef.current.viewId && allowed.length > 0) {
          const defaultViewId = tablesRef.current?.find((table) => table.publicId === tableId)?.defaultViewId
          const preferred =
            allowed.find((view) => view.publicId === defaultViewId) ??
            allowed.find((view) => view.isDefault) ??
            allowed[0]
          handlersRef.current.onViewChange(preferred.publicId)
        }
      })
      .catch((error) => {
        if (stale) return
        setViews([])
        // A source configured by another admin can point at a table this user can't open. That isn't an
        // error to report: the pickers show what the user can't see as restricted.
        if (error?.status !== 403) handlersRef.current.displayErrorMessage(error)
      })
    return () => {
      stale = true
    }
  }, [tableId])

  // The selected view, refetched whenever the view changes: its columns feed the column picker. The view's
  // own cached select options are not needed here (only the column list is), so they're left out.
  useEffect(() => {
    if (!viewId) return
    // A view picked after this one makes it stale: it's neither shown nor reported when it arrives.
    let stale = false
    api
      .getTableView(viewId, { ignoreCachedOptions: true })
      .then((response) => {
        if (stale) return
        setFetchedView({ viewId, view: response.data })
        handlersRef.current.onViewLoaded?.(response.data)
      })
      .catch((error) => {
        if (stale) return
        setFetchedView({ viewId, view: null })
        if (error?.status !== 403) handlersRef.current.displayErrorMessage(error)
      })
    return () => {
      stale = true
    }
  }, [viewId])

  // Each field has a row of its own at the dialog's width, a little apart.
  const fieldClass = 'flex min-w-0 flex-col gap-1.5'

  return (
    <div className="flex flex-col gap-3">
      <Field label="Table" htmlFor="source-table-select" className={fieldClass}>
        <Select
          inputId="source-table-select"
          options={tableOptions ?? []}
          {...tableSelection}
          onOptionClick={onTableChange}
          loading={tables === undefined}
          disabled={disabled}
        />
      </Field>

      <Field label="View" htmlFor="source-view-select" className={fieldClass}>
        <Select
          inputId="source-view-select"
          options={viewOptions ?? []}
          {...viewSelection}
          onOptionClick={onViewChange}
          disabled={disabled || !tableId}
          loading={Boolean(tableId) && views === undefined}
        />
      </Field>

      <Field label="Column" htmlFor="source-column-select" className={fieldClass}>
        <Select
          inputId="source-column-select"
          options={columnOptions ?? []}
          {...columnSelection}
          onOptionClick={onColumnChange}
          disabled={disabled || !viewId}
          loading={viewPending}
        />
      </Field>
    </div>
  )
}

export default SelectSourceFields
